import React from 'react';
import { ShowcaseSection, ShowcaseItem } from '../ShowcaseItem';
import {
  ExpandedCalendar,
  ExpandedCalendarHeader,
  ExpandedCalendarButton,
  ExpandedCalendarButtonText,
  ExpandedCalendarSelectedWeek,
  ExpandedCalendarBody,
  ExpandedCalendarDay,
  ExpandedCalendarDayHeader,
  ExpandedCalendarDayNumber,
  ExpandedCalendarWeekday,
  ExpandedCalendarHoursContainer
} from '@/components/custom/expanded-calendar/expanded-calendar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScheduleWrapper, Schedule, ScheduleItem, ScheduleDate, ScheduleInfos, ScheduleHeader, ScheduleTitle, ScheduleHour } from '@/components/custom/schedule/schedule';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';

const WEEK_DAYS = [
  { weekday: 'DOM', day: 13, isCurrent: false },
  { weekday: 'SEG', day: 14, isCurrent: false },
  { weekday: 'TER', day: 15, isCurrent: false },
  { weekday: 'QUA', day: 16, isCurrent: true },
  { weekday: 'QUI', day: 17, isCurrent: false },
  { weekday: 'SEX', day: 18, isCurrent: false },
  { weekday: 'SAB', day: 19, isCurrent: false }
];

const SAMPLE_EVENTS = {
  14: [{ title: 'Aula de Matematica', time: '09:00' }],
  16: [
    { title: 'Quiz Final', time: '10:30' },
    { title: 'Mentoria', time: '15:00' }
  ],
  18: [{ title: 'Aula ao vivo', time: '19:00' }]
};

export function CalendarsSection() {
  return (
    <ShowcaseSection
      id="calendars"
      title="Calendars"
      description="Componentes de calendario e agenda"
    >
      <ShowcaseItem
        label="ExpandedCalendar"
        description="Calendario expandido em visao semanal"
        previewClassName="p-0"
      >
        <ExpandedCalendar className="!min-h-0">
          <ExpandedCalendarHeader>
            <ExpandedCalendarButton label="Semana anterior">
              <FiChevronLeft />
              <ExpandedCalendarButtonText>Anterior</ExpandedCalendarButtonText>
            </ExpandedCalendarButton>
            <ExpandedCalendarSelectedWeek>13 - 19 de Abril 2026</ExpandedCalendarSelectedWeek>
            <ExpandedCalendarButton label="Proxima semana">
              <ExpandedCalendarButtonText>Proxima</ExpandedCalendarButtonText>
              <FiChevronRight />
            </ExpandedCalendarButton>
          </ExpandedCalendarHeader>
          <ExpandedCalendarBody>
            {WEEK_DAYS.map(({ weekday, day, isCurrent }) => (
              <ExpandedCalendarDay
                key={day}
                isCurrent={isCurrent}
              >
                <ExpandedCalendarDayHeader>
                  <ExpandedCalendarDayNumber isCurrent={isCurrent}>{day}</ExpandedCalendarDayNumber>
                  <ExpandedCalendarWeekday isCurrent={isCurrent}>{weekday}</ExpandedCalendarWeekday>
                </ExpandedCalendarDayHeader>
                <ExpandedCalendarHoursContainer>
                  {(SAMPLE_EVENTS[day] || []).map(event => (
                    <div
                      key={event.title}
                      className="flex flex-col gap-1 rounded-md bg-primary-lightest p-2"
                    >
                      <span className="text-[10px] font-bold text-primary">{event.time}</span>
                      <span className="text-xs text-primary-dark">{event.title}</span>
                    </div>
                  ))}
                </ExpandedCalendarHoursContainer>
              </ExpandedCalendarDay>
            ))}
          </ExpandedCalendarBody>
        </ExpandedCalendar>
      </ShowcaseItem>

      <ShowcaseItem
        label="Schedule (timeline vertical)"
        description="Linha do tempo de eventos com badges de data + variantes (default / success)"
      >
        <ScheduleWrapper>
          <Schedule>
            <ScheduleItem>
              <ScheduleDate variant="success">
                <div className="text-center">
                  <div className="text-lg leading-none">12</div>
                  <div className="text-[10px] leading-none">ABR</div>
                </div>
              </ScheduleDate>
              <ScheduleInfos>
                <ScheduleHeader>
                  <ScheduleTitle>Aula 1: Introducao a Algebra</ScheduleTitle>
                  <ScheduleHour>14:00</ScheduleHour>
                </ScheduleHeader>
                <p className="m-0 text-xs text-neutral">Conceitos fundamentais e nomenclatura</p>
              </ScheduleInfos>
            </ScheduleItem>
            <ScheduleItem>
              <ScheduleDate>
                <div className="text-center">
                  <div className="text-lg leading-none">15</div>
                  <div className="text-[10px] leading-none">ABR</div>
                </div>
              </ScheduleDate>
              <ScheduleInfos>
                <ScheduleHeader>
                  <ScheduleTitle>Aula 2: Equacoes do 1º Grau</ScheduleTitle>
                  <ScheduleHour>16:30</ScheduleHour>
                </ScheduleHeader>
                <p className="m-0 text-xs text-neutral">Resolucao passo-a-passo</p>
              </ScheduleInfos>
            </ScheduleItem>
            <ScheduleItem disabled>
              <ScheduleDate>
                <div className="text-center">
                  <div className="text-lg leading-none">19</div>
                  <div className="text-[10px] leading-none">ABR</div>
                </div>
              </ScheduleDate>
              <ScheduleInfos>
                <ScheduleHeader>
                  <ScheduleTitle>Aula 3: Funcoes Lineares</ScheduleTitle>
                  <ScheduleHour>14:00</ScheduleHour>
                </ScheduleHeader>
                <p className="m-0 text-xs text-neutral">Conceito + plotagem grafica</p>
              </ScheduleInfos>
            </ScheduleItem>
          </Schedule>
        </ScheduleWrapper>
      </ShowcaseItem>

      <ShowcaseItem
        label="Agenda do dia"
        description="Composicao alternativa: lista de eventos do dia"
      >
        <div className="max-w-md rounded-xl bg-white p-4 shadow-dropshadow">
          <div className="mb-3 flex items-center justify-between">
            <h4 className="m-0 text-sm font-bold text-primary-dark">Agenda - Quarta, 16 Abril</h4>
            <Badge
              variant="primary-light"
              size="xs"
            >
              3 eventos
            </Badge>
          </div>
          <div className="space-y-2">
            {[
              { time: '09:00', title: 'Aula de Algebra Linear', tag: 'Aulas', variant: 'primary-light' },
              { time: '10:30', title: 'Quiz Final do Modulo', tag: 'Avaliacao', variant: 'success' },
              { time: '15:00', title: 'Mentoria com prof. Carlos', tag: 'Mentoria', variant: 'secondary' }
            ].map(event => (
              <div
                key={event.title}
                className="flex items-center gap-3 rounded-lg border border-neutral-lightest bg-white p-3"
              >
                <div className="text-center">
                  <div className="text-xs font-bold text-primary">{event.time}</div>
                </div>
                <div className="flex-1">
                  <div className="text-sm font-bold text-primary-dark">{event.title}</div>
                  <Badge
                    variant={event.variant}
                    size="xs"
                  >
                    {event.tag}
                  </Badge>
                </div>
                <Button
                  variant="ghost"
                  size="xs"
                >
                  Ver
                </Button>
              </div>
            ))}
          </div>
        </div>
      </ShowcaseItem>
    </ShowcaseSection>
  );
}
